import { useEffect, useRef, useState } from 'react';
import { CModal, CModalBody, CModalHeader, CModalTitle, CSpinner } from '@coreui/react';
import { Node, NodeViewWrapper, ReactNodeViewRenderer, mergeAttributes, type ReactNodeViewProps } from '@tiptap/react';
import { downloadFile, formatSize } from '../../api/files';
import { attachmentUrl, isPdf, useMediaContract } from './media';

/**
 * Dva uzly editoru: obrázek a kartička souboru. Do HTML se ukládá jen id přílohy a název
 * (žádné src ani adresa) — to, co se smí zobrazit, rozhoduje server podle zakázky.
 *
 *   <img data-attachment-id="12" alt="pilíř.jpg">
 *   <div data-file-id="13" data-name="nabídka.pdf" data-type="application/pdf" data-size="52000"></div>
 */
export const AttachmentImage = Node.create({
  name: 'attachmentImage',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => Number(el.getAttribute('data-attachment-id')),
        renderHTML: (attrs) => ({ 'data-attachment-id': attrs.id }),
      },
      name: {
        default: '',
        parseHTML: (el) => el.getAttribute('alt') ?? '',
        renderHTML: (attrs) => ({ alt: attrs.name }),
      },
      type: {
        default: 'image/jpeg',
        parseHTML: (el) => el.getAttribute('data-type') ?? 'image/jpeg',
        renderHTML: (attrs) => ({ 'data-type': attrs.type }),
      },
    };
  },

  parseHTML: () => [{ tag: 'img[data-attachment-id]' }],
  renderHTML: ({ HTMLAttributes }) => ['img', mergeAttributes(HTMLAttributes)],
  addNodeView: () => ReactNodeViewRenderer(ImageView),
});

export const AttachmentFile = Node.create({
  name: 'attachmentFile',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      id: {
        default: 0,
        parseHTML: (el) => Number(el.getAttribute('data-file-id')),
        renderHTML: (attrs) => ({ 'data-file-id': attrs.id }),
      },
      name: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-name') ?? '',
        renderHTML: (attrs) => ({ 'data-name': attrs.name }),
      },
      type: {
        default: 'application/octet-stream',
        parseHTML: (el) => el.getAttribute('data-type') ?? 'application/octet-stream',
        renderHTML: (attrs) => ({ 'data-type': attrs.type }),
      },
      size: {
        default: 0,
        parseHTML: (el) => Number(el.getAttribute('data-size') ?? 0),
        renderHTML: (attrs) => ({ 'data-size': attrs.size }),
      },
    };
  },

  parseHTML: () => [{ tag: 'div[data-file-id]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes)],
  addNodeView: () => ReactNodeViewRenderer(FileView),
});

// ---------- zobrazeni ----------

function useAttachmentUrl(id: number, type: string) {
  const contractId = useMediaContract();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!contractId || !id) return;
    let alive = true;
    attachmentUrl(contractId, id, type)
      .then((u) => { if (alive) setUrl(u); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [contractId, id, type]);

  return { url, failed, contractId };
}

function ImageView({ node, selected }: ReactNodeViewProps) {
  const { id, name, type } = node.attrs as { id: number; name: string; type: string };
  const { url, failed } = useAttachmentUrl(id, type);
  const [open, setOpen] = useState(false);

  return (
    <NodeViewWrapper className={`rich-media${selected ? ' is-selected' : ''}`} data-drag-handle>
      {url ? (
        <img src={url} alt={name} className="rich-media-img" onClick={() => setOpen(true)} title="Zvětšit" />
      ) : (
        <div className="rich-media-placeholder">{failed ? `Obrázek ${name} se nepodařilo načíst` : <CSpinner size="sm" />}</div>
      )}
      {open && url && (
        <Preview title={name} onClose={() => setOpen(false)}>
          <img src={url} alt={name} className="rich-media-preview-img" />
        </Preview>
      )}
    </NodeViewWrapper>
  );
}

function FileView({ node, selected }: ReactNodeViewProps) {
  const { id, name, type, size } = node.attrs as { id: number; name: string; type: string; size: number };
  const pdf = isPdf(type);
  const { url, contractId } = useAttachmentUrl(pdf ? id : 0, type);
  const [open, setOpen] = useState(false);

  function activate() {
    if (pdf && url) setOpen(true);
    else if (contractId) void downloadFile(`/api/v1/contracts/${contractId}/attachments/${id}/content`, name);
  }

  return (
    <NodeViewWrapper className={`rich-media${selected ? ' is-selected' : ''}`} data-drag-handle>
      <button type="button" className="rich-file-card" onClick={activate} title={pdf ? 'Otevřít náhled' : 'Stáhnout'}>
        {pdf ? <PdfThumbnail url={url} /> : <span className="rich-file-icon" aria-hidden>{extension(name)}</span>}
        <span className="rich-file-meta">
          <span className="rich-file-name">{name}</span>
          <span className="rich-file-size">{formatSize(size)} · {pdf ? 'náhled' : 'stáhnout'}</span>
        </span>
      </button>
      {open && url && (
        <Preview title={name} onClose={() => setOpen(false)} wide>
          {/* Vestaveny prohlizec PDF; sandbox bez skriptu nasi stranky. */}
          <iframe src={url} title={name} className="rich-media-preview-pdf" />
        </Preview>
      )}
    </NodeViewWrapper>
  );
}

const extension = (name: string) => (name.split('.').pop() ?? '').slice(0, 4).toUpperCase() || 'SOUBOR';

/** Náhled první stránky PDF (pdf.js, Apache 2.0). Načte se, až když je nějaké PDF v textu. */
function PdfThumbnail({ url }: { url: string | null }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!url || !canvas.current) return;
    let alive = true;

    (async () => {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

      const data = await (await fetch(url)).arrayBuffer();
      const task = pdfjs.getDocument({ data });
      const doc = await task.promise;
      const page = await doc.getPage(1);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: 96 / base.width });

      const el = canvas.current;
      if (!alive || !el) return;
      el.width = viewport.width;
      el.height = viewport.height;
      await page.render({ canvas: el, viewport }).promise;
      void task.destroy();
    })().catch(() => { if (alive) setFailed(true); });

    return () => { alive = false; };
  }, [url]);

  if (failed || !url) return <span className="rich-file-icon" aria-hidden>{failed ? 'PDF' : <CSpinner size="sm" />}</span>;
  return <canvas ref={canvas} className="rich-file-thumb" aria-hidden />;
}

function Preview({ title, onClose, wide, children }: { title: string; onClose: () => void; wide?: boolean; children: React.ReactNode }) {
  return (
    <CModal visible onClose={onClose} size={wide ? 'xl' : 'lg'} alignment="center">
      <CModalHeader><CModalTitle className="text-truncate">{title}</CModalTitle></CModalHeader>
      <CModalBody className="text-center p-2">{children}</CModalBody>
    </CModal>
  );
}
